import warehouseIcon from '../../../assets/images/warehouseBlack.svg';
import preflightIcon from '../../../assets/images/preflightBlack.svg';
import postFlightIcon from '../../../assets/images/flight_landBlack.svg';
import deliveryIcon from '../../../assets/images/deliveryBlack.svg';
import monitoringIcon from '../../../assets/images/languageBlack.svg';
import { AlertType, getAlertTypeLabel } from '@/common/alertType';

/**
 * Condition type for alert rules
 */
type ConditionType = 'Over' | 'Under' | 'Below' | 'Within' | 'From' | null;

/**
 * Alert rule configuration
 */
export interface AlertRule {
  /** Unique identifier for the alert rule */
  id: AlertType;
  /** Display title for the alert */
  title: string;
  /** Detailed description of when the alert triggers */
  description: string;
  /** Type of condition (e.g., Over, Under, Within) */
  conditionType: ConditionType;
  /** Unit of measurement (e.g., 'mins', '%', 'ft') */
  unit: string;
  /** Minimum value for the threshold */
  min: number;
  /** Maximum value for the threshold */
  max: number;
  /** Whether to show reset icon */
  showResetIcon: boolean;
  /** Whether to show info icon */
  showInfoIcon?: boolean;
  /** Whether to show sensor info icon */
  showSensorInfoIcon?: boolean;
  /** Whether sensor info icon is disabled */
  showSensorInfoIconDisabled?: boolean;
  /** Whether to show setup sensor icon */
  showSetupSensorIcon?: boolean;
  /** Optional note about sensor requirements */
  sensorNote?: string;
}

/**
 * Alert category grouping multiple related rules
 */
export interface AlertCategory {
  /** Category name */
  category: string;
  /** Icon representing the category */
  icon: string;
  /** Alert rules in this category */
  rules: AlertRule[];
}

/**
 * Configuration for alert customization organized by shipment lifecycle stage
 */
export const AlertCustomizationConfig: AlertCategory[] = [
  {
    category: 'Pre-pick',
    icon: warehouseIcon,
    rules: [
      {
        id: 'low_battery_at_origin',
        title: getAlertTypeLabel('low_battery_at_origin'),
        description:
          'When a shipment’s sensor battery is below the set threshold at origin',
        conditionType: 'Under',
        unit: '%',
        min: 0,
        max: 100,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
    ],
  },
  {
    category: 'Pre-flight',
    icon: preflightIcon,
    rules: [
      {
        id: 'driver_late_to_pickup',
        title: getAlertTypeLabel('driver_late_to_pickup'),
        description:
          'When a driver missed the expected pick-up time by a certain amount of time',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showInfoIcon: false,
        showResetIcon: true,
      },
      {
        id: 'flight_delayed_beyond_threshold',
        title: getAlertTypeLabel('flight_delayed_beyond_threshold'),
        description:
          'When a flight has been delayed beyond a specified duration',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showInfoIcon: false,
        showResetIcon: true,
      },
      {
        id: 'shipment_not_loaded_onto_flight',
        title: getAlertTypeLabel('shipment_not_loaded_onto_flight'),
        description:
          'When the shipment’s location data and flight’s location data diverge for longer than the set period, indicating the shipment likely wasn’t loaded onto the plane',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'sensor_location_still_at_pickup',
        title: getAlertTypeLabel('sensor_location_still_at_pickup'),
        description:
          'When a sensor is still reporting near the pickup airport after the flight has departed',
        conditionType: 'Within',
        unit: 'mi',
        min: 0,
        max: 100,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
    ],
  },
  {
    category: 'Post-flight',
    icon: postFlightIcon,
    rules: [
      {
        id: 'offload_delay_after_flight_arrival',
        title: getAlertTypeLabel('offload_delay_after_flight_arrival'),
        description:
          'When unloading the shipment from a flight takes longer than expected',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        sensorNote: 'Sensor optional',
        showResetIcon: true,
      },
      {
        id: 'driver_late_to_recover_shipment',
        title: getAlertTypeLabel('driver_late_to_recover_shipment'),
        description:
          'When the shipment is taking longer than expected to get picked up by a driver after offboarding confirmation',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showResetIcon: true,
      },
      {
        id: 'sensor_location_not_at_drop',
        title: getAlertTypeLabel('sensor_location_not_at_drop'),
        description:
          'When sensor readings after flight arrival stay beyond the allowed distance from the drop location',
        conditionType: 'Over',
        unit: 'mi',
        min: 0,
        max: 100,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
    ],
  },
  {
    category: 'At delivery',
    icon: deliveryIcon,
    rules: [
      {
        id: 'driver_cant_find_location',
        title: getAlertTypeLabel('driver_cant_find_location'),
        description:
          'When a driver reaches the destination vicinity but hasn’t completed the delivery within the expected time',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'delivered_to_wrong_address',
        title: getAlertTypeLabel('delivered_to_wrong_address'),
        description:
          'When delivery location deviates from address on file by more than the allowed distance',
        conditionType: 'Over',
        unit: 'ft',
        min: 100,
        max: 2000,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: false,
        showSetupSensorIcon: false,
        sensorNote: 'Sensor optional',
        showResetIcon: true,
      },
      {
        id: 'delivered_to_wrong_recipient',
        title: getAlertTypeLabel('delivered_to_wrong_recipient'),
        description:
          'When the name on the proof of delivery (POD) does not match the intended recipient’s name on file',
        conditionType: null,
        unit: '',
        min: 0,
        max: 999,
        showResetIcon: true,
      },
    ],
  },
  {
    category: 'Continuous monitoring',
    icon: monitoringIcon,
    rules: [
      {
        id: 'low_battery',
        title: getAlertTypeLabel('low_battery'),
        description:
          'When a sensor’s battery level drops below the defined threshold',
        conditionType: 'Below',
        unit: '%',
        min: 0,
        max: 100,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'flight_landing_close_to_cargo_closing_time',
        title: getAlertTypeLabel('flight_landing_close_to_cargo_closing_time'),
        description:
          'When a Flight’s expected arrival time is too close to cargo closing hours at the recovery airport',
        conditionType: 'Under',
        unit: 'mins',
        min: 0,
        max: 120,
        showResetIcon: true,
      },
      {
        id: 'weather_event_impact_expected',
        title: getAlertTypeLabel('weather_event_impact_expected'),
        description:
          'When the shipment is expected to cross a weather event of a certain severity, defined on a scale of 1-5, with Level 1 having minimal impact, Level 5 having extreme/catastrophic impact. Re-routing decisions typically implemented starting Level 3',
        conditionType: 'From',
        unit: 'Level',
        min: 1,
        max: 5,
        showResetIcon: true,
      },
      {
        id: 'status_update_overdue',
        title: getAlertTypeLabel('status_update_overdue'),
        description:
          'When the time elapsed without a status update exceeds a multiple of the historical median, flagging unexpected delays (min 10 shipments baseline required).',
        conditionType: 'Over',
        unit: 'X',
        min: 1,
        max: 999,
        showResetIcon: true,
      },
      {
        id: 'vehicle_idle_in_transit',
        title: getAlertTypeLabel('vehicle_idle_in_transit'),
        description:
          'When a vehicle has remained idle for a certain amount of time while in transit',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'temperature_nearing_limit',
        title: getAlertTypeLabel('temperature_nearing_limit'),
        description:
          'When the shipment’s temperature is within the defined margin of its allowable range, indicating it’s nearing an excursion threshold',
        conditionType: 'Within',
        unit: '%',
        min: 0,
        max: 100,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'temperature_excursion_detected',
        title: getAlertTypeLabel('temperature_excursion_detected'),
        description:
          'When the shipment’s temperature goes outside its allowable range for longer than the specified duration',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: true,
        showResetIcon: true,
      },
      {
        id: 'temperature_excursion_pre_alarm',
        title: getAlertTypeLabel('temperature_excursion_pre_alarm'),
        description:
          'When temperature is projected to exceed the allowed range within the projection window',
        conditionType: 'Within',
        unit: 'mins',
        min: 0,
        max: 720,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: true,
        showResetIcon: true,
      },
      {
        id: 'viability_window_risk_expiring',
        title: getAlertTypeLabel('viability_window_risk_expiring'),
        description:
          'When a Product viability window is too close to the anticipated transit time left',
        conditionType: 'Within',
        unit: 'mins',
        min: 0,
        max: 120,
        showResetIcon: true,
      },
      {
        id: 'viability_at_risk_before_arrival',
        title: getAlertTypeLabel('viability_at_risk_before_arrival'),
        description:
          'When the shipment is projected to arrive with less than threshold minutes of viability',
        conditionType: 'Within',
        unit: 'mins',
        min: 0,
        max: 720,
        showResetIcon: true,
      },
      {
        id: 'viability_window_nearly_exhausted',
        title: getAlertTypeLabel('viability_window_nearly_exhausted'),
        description:
          'When the shipment’s remaining viability time is close to being exhausted, signaling an imminent risk to product stability',
        conditionType: 'Under',
        unit: 'mins',
        min: 0,
        max: 120,
        showResetIcon: true,
      },
      {
        id: 'viability_window_exhausted',
        title: getAlertTypeLabel('viability_window_exhausted'),
        description:
          'When the shipment’s viability time is exhausted for longer than the specified duration',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: false,
        showSetupSensorIcon: true,
        showResetIcon: true,
      },
      {
        id: 'excessive_light_exposure_detected',
        title: getAlertTypeLabel('excessive_light_exposure_detected'),
        description:
          'When the shipment’s light exposure goes beyond its allowable maximum value for longer than the specified duration',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'excessive_tilt_detected',
        title: getAlertTypeLabel('excessive_tilt_detected'),
        description:
          'When the shipment has been titled beyond its maximum value for longer than the specified duration',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'excessive_pressure_detected',
        title: getAlertTypeLabel('excessive_pressure_detected'),
        description:
          'When the shipment’s pressure goes outside its allowable range for longer than the specified duration',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'excessive_humidity_detected',
        title: getAlertTypeLabel('excessive_humidity_detected'),
        description:
          'When the shipment’s humidity goes outside its allowable range for longer than the specified duration',
        conditionType: 'Over',
        unit: 'mins',
        min: 0,
        max: 120,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
      {
        id: 'excessive_shock_detected',
        title: getAlertTypeLabel('excessive_shock_detected'),
        description:
          'When a sensor detects an impact or shock that exceeds the configured shock threshold we will notify your right away',
        conditionType: null,
        unit: '',
        min: 0,
        max: 999,
        showSensorInfoIcon: false,
        showSensorInfoIconDisabled: true,
        showSetupSensorIcon: false,
        showResetIcon: true,
      },
    ],
  },
];
